// Shared DocsBot loader. The widget is a page-level singleton - it renders itself into a
// single #docsbotai-root element - so every feature that needs the chatbot (the trigger
// buttons on widgets, the inline embed) has to go through here to avoid mounting it twice.
const CHAT_SCRIPT_URL = 'https://widget.docsbot.ai/chat.js';
export const DOCSBOT_ROOT_SELECTOR = '#docsbotai-root';
let pendingMount = null;
let mountedBotId = null;
function loadScript(src) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = src;
        script.addEventListener('load', () => resolve());
        script.addEventListener('error', () => reject(new Error(`Could not load ${src}`)));
        document.head.appendChild(script);
    });
}
/** Long enough for a slow connection, short enough that a broken widget stops being pending. */
const ROOT_ELEMENT_TIMEOUT_MS = 15000;
// The widget renders itself asynchronously after mounting, so wait for its root element before
// treating the chatbot as ready. Rejects rather than waiting forever, so a widget that never
// renders does not leave the mount permanently pending and block every later attempt.
function waitForElement(selector, timeoutMs) {
    return new Promise((resolve, reject) => {
        if (document.querySelector(selector)) {
            resolve();
            return;
        }
        const observer = new MutationObserver(() => {
            if (document.querySelector(selector)) {
                finish(resolve);
            }
        });
        const timeout = window.setTimeout(() => finish(() => reject(new Error(`${selector} did not appear within ${timeoutMs}ms`))), timeoutMs);
        function finish(settle) {
            window.clearTimeout(timeout);
            observer.disconnect();
            settle();
        }
        observer.observe(document.body, { childList: true, subtree: true });
    });
}
/**
 * Loads and mounts the chatbot. The first caller wins - a page can only ever host one
 * DocsBot instance, so later callers get the already pending mount instead of a second bot.
 */
export function mountDocsBot(options) {
    if (pendingMount) {
        if (mountedBotId !== options.id) {
            console.warn(`Chatbot "${mountedBotId}" is already on this page, so "${options.id}" was ignored. ` +
                'Only one chatbot can be mounted per page.');
        }
        return pendingMount;
    }
    mountedBotId = options.id;
    const mount = (async () => {
        await loadScript(CHAT_SCRIPT_URL);
        // The script can load without leaving the API behind - a cached empty response, a proxy, a
        // changed snippet. Failing here keeps the mount from staying pending forever.
        if (!window.DocsBotAI?.mount) {
            throw new Error(`${CHAT_SCRIPT_URL} loaded but DocsBotAI.mount is not available`);
        }
        await window.DocsBotAI.mount(options);
        await waitForElement(DOCSBOT_ROOT_SELECTOR, ROOT_ELEMENT_TIMEOUT_MS);
    })();
    pendingMount = mount;
    // Let a later attempt retry if this one failed.
    mount.catch(() => {
        pendingMount = null;
        mountedBotId = null;
    });
    return mount;
}
/** True when something already mounted the chatbot on this page. */
export function isDocsBotMounted() {
    return pendingMount !== null || document.querySelector(DOCSBOT_ROOT_SELECTOR) !== null;
}
/**
 * An ISO date-time with no timezone on it, such as `2026-08-06T13:49:28.514357`, which is what
 * DocsBot sends.
 */
const NAKED_ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
function parseTimestamp(timestamp) {
    // DocsBot timestamps are UTC but carry no offset, and JavaScript reads an offset-less
    // date-time as local time - which showed chat times two hours behind the visitor's clock
    // through a European summer. Say UTC explicitly so toLocaleTimeString converts properly.
    if (typeof timestamp === 'string' && NAKED_ISO_DATE_TIME.test(timestamp)) {
        return new Date(`${timestamp}Z`);
    }
    return new Date(timestamp);
}
function formatTimestamp(timestamp) {
    if (timestamp === undefined || timestamp === null) {
        return '';
    }
    const date = parseTimestamp(timestamp);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    return `[${time}] `;
}
/**
 * Turns the chat history into a plain-text transcript. Entries carry the message under
 * either a Human or an AI key.
 */
export function formatHistory(history) {
    if (!Array.isArray(history)) {
        return '';
    }
    return history
        .map((entry) => {
        if (!entry) {
            return '';
        }
        const isHuman = typeof entry.Human === 'string';
        const text = (isHuman ? entry.Human : entry.AI) ?? '';
        if (!text.trim()) {
            return '';
        }
        return `${formatTimestamp(entry.timestamp)}${isHuman ? 'Human' : 'AI'}: ${text.trim()}`;
    })
        .filter((line) => line.length > 0)
        .join('\n\n');
}
